/**
 * Tabs is a container that must wrap TabsList and TabPanels
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices-1.1/#tabpanel
 */
import React, { Component, ReactNode, createContext, RefObject, ReactElement } from "react";
import { observer } from "mobx-react";
import { action, computed, observable } from "mobx";
import { randomString } from "../../../utils";
import { isNil } from "lodash";

interface BaseTabsProps {
  children?: ReactNode;
}

interface TabsControlledProps extends BaseTabsProps {
  /**
   * When a selected index is provided,
   * this will be used to set the
   * current tab.
   */
  selectedIndex: number;
  onChange: (index: number) => void;
}

interface TabsUncontrolledProps extends BaseTabsProps {
  onChange?: (index: number) => void;
  initialIndex?: number;
  selectedIndex?: never;
}

type TabsProps = TabsControlledProps | TabsUncontrolledProps;

const isControlledProps = (props: TabsProps): props is TabsControlledProps =>
  !isNil((props as any).selectedIndex) && !isNil((props as any).onChange);

const isUncontrolledProps = (props: TabsProps): props is TabsUncontrolledProps => !isControlledProps(props);

export interface ITabIndicator {
  left: number;
  width: number;
}

export interface ITabsContext {
  selectedIndex?: number;
  setSelectedIndex: (index: number) => void;
  prevSelectedIndex?: number;
  tabRefs: RefObject<HTMLElement>[];
  focusPanel: () => void;
  addTabRef: (tabRef: RefObject<HTMLButtonElement>, index: number) => void;
  removeTabRef: (tabRef: RefObject<HTMLButtonElement>) => void;
  setPanelRef: (panelRef: RefObject<HTMLDivElement>) => void;
  id: string;
  panelHeight?: number;
  updatePanelHeight: (height: number) => void;
  indicator?: ITabIndicator;
  setIndicator: (indicator: ITabIndicator) => void;
}

export const TabsContext = createContext({} as ITabsContext);

@observer
class Tabs extends Component<TabsProps> {
  constructor(props: TabsProps) {
    super(props);

    if (isControlledProps(props)) {
      this._selectedIndex = props.selectedIndex;
    } else if (isUncontrolledProps(props)) {
      this._selectedIndex = props.initialIndex ?? 0;
    } else {
      this._selectedIndex = 0;
    }
  }

  private id = randomString();

  @observable.ref
  private panelRef: RefObject<HTMLElement> | undefined;

  @observable.ref
  private tabRefs: RefObject<HTMLElement>[] = [];

  @observable.ref
  private prevSelectedIndex: number | undefined;

  @observable.ref
  private _selectedIndex: number;

  @observable.ref
  private panelHeight: number | undefined;

  @observable.ref
  private indicator: ITabIndicator | undefined;

  @action
  private setSelectedIndex = (index: number): void => {
    this.prevSelectedIndex = this._selectedIndex;
    this._selectedIndex = index;
    this.props.onChange?.(index);
  };

  @action
  private addTabRef = (tabRef: RefObject<HTMLButtonElement>, index: number): void => {
    this.tabRefs = [...this.tabRefs.slice(0, index), tabRef, ...this.tabRefs.slice(index)];
  };

  @action
  private removeTabRef = (tabRef: RefObject<HTMLButtonElement>): void => {
    const newTabRefs = this.tabRefs.filter((ref) => ref !== tabRef);
    this.tabRefs = newTabRefs;
  };

  @action
  private setPanelRef = (panelRef: RefObject<HTMLDivElement>): void => {
    this.panelRef = panelRef;
  };

  @action
  private setIndicator = (indicator: ITabIndicator): void => {
    this.indicator = indicator;
  };

  @action
  private updatePanelHeight = (height: number): void => {
    this.panelHeight = height;
  };

  private focusPanel = (): void => {
    this.panelRef?.current?.focus();
  };

  @computed
  private get selectedIndex() {
    if (isControlledProps(this.props)) {
      return this.props.selectedIndex;
    }
    return this._selectedIndex;
  }

  public componentDidUpdate(prevProps: BaseTabsProps): void {
    if (isControlledProps(this.props) && !isControlledProps(prevProps)) {
      console.error(
        "Tabs component changed from uncontrolled to controlled component; this may cause unexpected behaviour.",
      );
    }
  }

  public render(): ReactNode {
    return (
      <TabsContext.Provider
        value={{
          selectedIndex: this.selectedIndex,
          setSelectedIndex: this.setSelectedIndex,
          prevSelectedIndex: this.prevSelectedIndex,
          tabRefs: this.tabRefs,
          focusPanel: this.focusPanel,
          addTabRef: this.addTabRef,
          removeTabRef: this.removeTabRef,
          setPanelRef: this.setPanelRef,
          panelHeight: this.panelHeight,
          id: this.id,
          updatePanelHeight: this.updatePanelHeight,
          indicator: this.indicator,
          setIndicator: this.setIndicator,
        }}
      >
        <div className="tabs">{this.props.children}</div>
      </TabsContext.Provider>
    );
  }
}

export interface IWithTabsProps {
  tabs: ITabsContext;
}

/**
 * HOC for getting tab context state
 */
export function withTabs<P extends IWithTabsProps = IWithTabsProps>(Component: React.ComponentType<P>) {
  return function WrapperComponent(props: Omit<P, keyof IWithTabsProps>): ReactElement {
    return (
      // @TODO (yundi, 2020-10-20): props as P is wrong, will figure out how to fix it with strict typing
      <TabsContext.Consumer>
        {(context: ITabsContext): ReactNode => <Component {...(props as P)} tabs={context} />}
      </TabsContext.Consumer>
    );
  };
}

export default Tabs;
